import React, { useCallback, useState } from 'react';
import { isNull } from 'lodash';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';

import {
  CheckboxGroupInput,
  RecordContextProvider,
  useCreate,
  useGetOne,
  useNotify,
  Form,
  useRecordContext,
  SelectArrayInput,
  BooleanInput,
  required,
  FormDataConsumer,
  usePermissions,
} from 'react-admin';

import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import { DateTime } from 'luxon';

import useDialogStatus from '../hooks/useDialogStatus';
import reportTypes from '../constants/reportTypes';
import includeSheetsConstant, { IncludeSheets, includeSheetsChoices } from '../constants/includeSheets';
import { ENTITY_REPORT_GENERATING_MESSAGE } from '../constants/reportMessages';
import DownloadModalReports from './DownloadModalReports';
import currencyCodes, { Currencies } from '../constants/currencies';
import SmartDateTimeInput from './SmartDateTimeInput';
import InfoPopover from '../components/layout/InfoPopover';
import actions, { Actions } from '../constants/actions';
import { attributesSchema, checkIsTypeAllowed } from '../utilities/reports/permissions';
import hasAccess from '../utilities/hasAccess';
import dateParse, { dateFormat } from '../components/utils/dateParser';

const dateRangeValidation = (_value: string, allValues: {
  from: string;
  to: string;
}) => {
  const { from, to } = allValues;
  if (
    from
    && to
  ) {
    if (DateTime.fromISO(from) > DateTime.fromISO(to)) {
      return 'from date should be less than to date';
    }
  }
  return undefined;
};

const isPastDate = (value: string) => {
  if (DateTime.fromISO(value) > DateTime.now()) return 'Date should be in the past';
  return undefined;
};

const ReportModal = () => {
  const [reportId, setReportId] = useState();
  const record = useRecordContext();

  const { permissions } = usePermissions<Actions[]>();

  const canCreate = hasAccess(permissions, actions.REPORT_CREATE, {
    schema: attributesSchema,
    check: checkIsTypeAllowed(reportTypes.ENTITY_REPORT),
  });

  const {
    open,
    openDialog,
    closeDialog,
  } = useDialogStatus();

  const [createReport] = useCreate();
  const notify = useNotify();

  const handleClose = useCallback(() => {
    closeDialog();
    setReportId(undefined);
  }, [closeDialog, setReportId]);

  const handleSubmit = useCallback((
    {
      includeSheets,
      from,
      to,
      currencies,
      associatedCases,
    }: {
      includeSheets?: IncludeSheets;
      from?: Date;
      to?: Date;
      currencies?: Currencies[];
      associatedCases?: boolean;
    },
  ) => {
    if (!canCreate) return;

    createReport(
      'reports/',
      {
        data: {
          type: reportTypes.ENTITY_REPORT,
          report: {
            includeSheets,
            entityId: record?.id,
            from: isNull(from) ? undefined : from,
            to: isNull(to) ? undefined : to,
            currencies,
            associatedCases: includeSheets!.includes(includeSheetsConstant.CASE_LIST)
              ? associatedCases
              : undefined,
          },
        },
      },
      {
        onSuccess: (res) => {
          setReportId(res.id);
        },
        onError: () => {
          notify('Failed to generate entity report', { type: 'error' });
        },
      },
    );
  }, [notify, createReport, record?.id, setReportId, canCreate]);

  const { data } = useGetOne(
    'reports',
    { id: reportId },
    {
      enabled: !!reportId,
      refetchInterval: 5000,
    },
  );

  if (!canCreate) return null;

  return (
    <>
      <Button
        variant="contained"
        onClick={openDialog}
        startIcon={<DescriptionIcon />}
      >
        Entity report
      </Button>
      <Dialog onClose={handleClose} open={open} maxWidth="sm" fullWidth>
        <Form
          onSubmit={handleSubmit}
          record={{
            includeSheets: Object.values(includeSheetsConstant),
            currencies: [],
          }}
        >
          <Box padding={4}>
            <Box display="flex" justifyContent="space-between">
              <DialogTitle sx={{ padding: 0 }} fontWeight="bold">Generate entity report</DialogTitle>
              <IconButton size="medium" onClick={handleClose} sx={{ padding: 0 }}>
                <CloseIcon />
              </IconButton>
            </Box>
            <DialogContentText>
              Choose which information to include in the report.
            </DialogContentText>
          </Box>
          <DialogContent sx={{ py: 2, px: 4 }}>
            <Typography variant="subtitle1" fontWeight="bold" marginBottom={2}>
              Filters
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SmartDateTimeInput
                  label="From"
                  source="from"
                  validate={[isPastDate, dateRangeValidation]}
                  parse={dateParse}
                  format={dateFormat}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <SmartDateTimeInput
                  label="To"
                  source="to"
                  validate={[isPastDate, dateRangeValidation]}
                  parse={dateParse}
                  format={dateFormat}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <SelectArrayInput
                  choices={currencyCodes.map((curr) => ({
                    name: curr,
                    id: curr,
                  }))}
                  fullWidth
                  source="currencies"
                  label="Currencies"
                />
              </Grid>
            </Grid>
            <Typography variant="subtitle1" fontWeight="bold">
              Content to include
            </Typography>
            <CheckboxGroupInput
              label=""
              source="includeSheets"
              row={false}
              choices={includeSheetsChoices}
              margin="none"
              fullWidth
              validate={required()}
              helperText={false}
            />
            <FormDataConsumer<{ includeSheets: IncludeSheets[] }>>
              {({ formData }) => {
                if (
                  formData.includeSheets.some((sheet) => sheet === includeSheetsConstant.CASE_LIST)
                ) {
                  return (
                    <Grid container>
                      <Grid size={{ xs: 9, sm: 6 }}>
                        <Typography variant="smallTitle">
                          Include associated cases
                        </Typography>
                        <InfoPopover
                          title="Associated Cases"
                          content="Associated Cases are cases where the Entity in question has a role in a related transaction to another case, but is not the subject of the case (such as recipient, initiator or processor of a suspicious transaction)"
                        />
                      </Grid>
                      <Grid size={{ xs: 3, sm: 6 }} display="flex" justifyContent="flex-end">
                        <BooleanInput source="associatedCases" label="" helperText={false} />
                      </Grid>
                    </Grid>
                  );
                }
                return null;
              }}
            </FormDataConsumer>
            {reportId && (
              <DialogContentText margin={2}>
                {ENTITY_REPORT_GENERATING_MESSAGE}
              </DialogContentText>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: 4 }}>
            {!reportId && (<Button variant="outlined" onClick={handleClose} sx={{ margin: 2 }}>Cancel</Button>)}
            <RecordContextProvider value={data}>
              <DownloadModalReports reportType={reportTypes.ENTITY_REPORT} />
            </RecordContextProvider>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
};

export default ReportModal;
